import { css } from 'aphrodite';
import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { useDebounceValue } from '../../../hooks/useDebounceValue';
import { useLocalStorageState } from '../../../hooks/useLocalStorage';
import { useAdminUserSession } from '../../../models/hooks/adminStateHooks';
import { MastheadPortalId } from '../../../models/AdminModels';
import { AgentRole, TicketStatus } from '../../../models/Support/SupportEnums';
import { useMyAgentConfigQuery } from '../../../queries/Support/useSupportAgentConfig';
import {
	toAgentTicketCountMap,
	useSupportAgentTicketCountsQuery,
} from '../../../queries/Support/useSupportAgentTicketCounts';
import { useSupportCategoriesQuery } from '../../../queries/Support/useSupportCategories';
import { useSupportInboxCounts } from '../../../queries/Support/useSupportInboxNav';
import { useSupportMailboxListQuery } from '../../../queries/Support/useSupportMailboxQuery';
import { sortSupportAgentsByName, useSupportTeamQuery } from '../../../queries/Support/useSupportTeam';
import { useSupportTicketQuery } from '../../../queries/Support/useSupportTicketQuery';
import { useSupportTicketSearchQuery } from '../../../queries/Support/useSupportTicketSearch';
import { useSupportTicketsQuery } from '../../../queries/Support/useSupportTicketsQuery';
import { useSupportTicketsRealtime } from '../../../queries/Support/useSupportTicketsRealtime';
import { Portal } from '../../../web/components/Portal';
import { AdminDrawerToolbar, SideDrawer } from '../../../web/components-v2/Drawer/AppDrawer';
import { AdminSupportPageLayout } from '../../components/supportShared/AdminSupportPageLayout';
import { NewTicketModal } from '../../components/supportShared/NewTicketModal';
import type { NewTicketMode } from '../../components/supportShared/NewTicketModal/newTicketDraft';
import {
	LAST_MAILBOX_STORAGE_KEY,
	TICKET_LIST_PINNED_STORAGE_KEY,
} from '../../components/supportShared/supportStorageKeys';
import { ALL_MAILBOXES_VALUE, SupportMastheadAppBar } from '../../components/supportShared/SupportMastheadAppBar';
import { SupportNewTicketDrawerButton } from '../../components/supportShared/SupportNewTicketDrawerButton';
import { ChangeCustomerModal } from './ChangeCustomerModal';
import { ConversationPane } from './ConversationPane';
import { PhoneTicketStage } from './PhoneTicketStage';
import { DEFAULT_INBOX_FILTERS, type InboxFilters, type InboxView, sortToListParams } from './inboxFilters';
import { SupportReportsScreen } from './Reports/SupportReportsScreen';
import { RightPanel } from './RightPanel';
import { inboxStyles as s, TICKET_LIST_RAIL_WIDTH_PX, TICKET_LIST_WIDTH_PX } from './styles/inboxStyles';
import { TicketListPane, type TicketListSource } from './TicketListPane';
import { TicketTableView } from './TicketTableView';
import { TicketTopBar } from './TicketTopBar';

const PAGE_SIZE = 25;
const CATEGORY_FILTER_PREFIX = 'category:';

/** Human labels for the standard inbox filters surfaced as views. */
const FILTER_LABELS: Record<string, string> = {
	all: 'All tickets',
	unassigned: 'Unassigned',
	mine: 'My tickets',
	priority: 'Priority',
	assigned: 'Assigned',
	closed: 'Closed',
	spam: 'Spam',
	archive: 'Archive',
};

const parsePageParam = (value: string | null) => {
	const page = Number(value);
	return Number.isInteger(page) && page > 0 ? page : 1;
};

/**
 * The views that define themselves by who a ticket is assigned to. An explicit assignee refinement
 * replaces that view's own constraint (see `buildSupportTicketFilterRequest`), so the selected view
 * has to follow the refinement — otherwise Mine stays lit over someone else's queue. Views that own
 * another dimension, like Closed, keep their constraint and simply narrow.
 */
const ASSIGNEE_OWNING_VIEWS = new Set(['unassigned', 'mine', 'assigned']);

const getViewForAssignee = (assigneeId: string | undefined, currentUserId: string | undefined) => {
	// No assignee means "Anyone", which hands the dimension back to whichever view is selected.
	if (!assigneeId) return undefined;
	return assigneeId === currentUserId ? 'mine' : 'assigned';
};

const clearMailboxScopedFilters = (filters: InboxFilters): InboxFilters => ({
	...filters,
	categoryId: undefined,
	assigneeId: undefined,
});

export const AdminSupportInboxPage: React.FC = () => {
	const location = useLocation();
	const history = useHistory();
	const isPhoneLayout = useMediaQuery({ maxWidth: 767 });
	// Reads the same AppState session the ticket-list query builds the Mine view from, so the view
	// this picks can never disagree with the assignee the query actually filters on.
	const currentUserId = useAdminUserSession().user?.id;
	const params = new URLSearchParams(location.search);
	const accountId = params.get('accountId') || undefined;
	const accountName = params.get('accountName') || undefined;
	const activeFilter = params.get('filter') || (accountId ? 'all' : 'unassigned');
	const selectedTicketId = params.get('ticketId') || undefined;
	const targetThreadId = params.get('threadId') || undefined;
	const currentPage = parsePageParam(params.get('page'));
	const screen = params.get('screen') === 'reports' ? 'reports' : 'inbox';

	const viewParam = params.get('view');
	// Phone is a navigation shell, not a compressed table. The URL remains compatible with desktop.
	const inboxView: InboxView = isPhoneLayout || viewParam === 'desk' ? 'desk' : 'table';

	// Push what an agent would expect Back to undo — the Inbox/Reports tabs, the table/desk switch,
	// opening a ticket. Replace state within a screen: filters, pagination, thread selection, and every
	// redirect this page performs on the agent's behalf.
	const setParams = React.useCallback(
		(updates: Record<string, string | null>, options?: { push?: boolean }) => {
			const next = new URLSearchParams(location.search);
			for (const [key, value] of Object.entries(updates)) {
				if (value === null) next.delete(key);
				else next.set(key, value);
			}
			const search = next.toString();
			// The tabs and the ticket list stay clickable when already selected, so a push can land on the
			// URL already on screen. That entry gives Back nothing to do until it has been popped.
			if (search === new URLSearchParams(location.search).toString()) return;
			const path = search ? `/support?${search}` : '/support';
			if (options?.push) {
				history.push(path);
			} else {
				history.replace(path);
			}
		},
		[history, location.search]
	);

	// Notification deep links omit mailboxId, so prefer the last valid mailbox before the first active one.
	const { data: mailboxes } = useSupportMailboxListQuery();
	const mailboxList = mailboxes ?? [];
	const requestedMailboxId = params.get('mailboxId');
	const [storedMailboxId, setStoredMailboxId] = useLocalStorageState<string>(LAST_MAILBOX_STORAGE_KEY, '');
	const isKnownMailbox = (id: string | null): id is string => !!id && mailboxList.some(m => m.id === id);
	const defaultMailboxId = mailboxList.find(m => m.isActive)?.id ?? mailboxList[0]?.id ?? '';
	const selectedMailboxId = isKnownMailbox(requestedMailboxId)
		? requestedMailboxId
		: isKnownMailbox(storedMailboxId)
			? storedMailboxId
			: defaultMailboxId;

	React.useEffect(() => {
		if (!selectedMailboxId) return;
		setStoredMailboxId(selectedMailboxId);
	}, [selectedMailboxId, setStoredMailboxId]);

	useSupportTicketsRealtime(selectedMailboxId);
	const navItems = useSupportInboxCounts(selectedMailboxId);
	const categoriesQuery = useSupportCategoriesQuery(selectedMailboxId);
	// Depend on the react-query data reference (stable between renders) rather than a fresh `?? []`.
	const categoryValues = categoriesQuery.data?.values;
	const categories = categoryValues ?? [];
	const categoryMap = React.useMemo(() => {
		const map: Record<string, { name: string; color?: string }> = {};
		for (const c of categoryValues ?? []) map[c.id] = { name: c.name, color: c.color };
		return map;
	}, [categoryValues]);

	const requestedCategoryView = activeFilter.startsWith(CATEGORY_FILTER_PREFIX);
	const mailboxUrlNeedsReset =
		requestedCategoryView || params.has('page') || params.has('ticketId') || params.has('threadId');

	// Free-text search and the Search criteria popover both live in local state (not the URL). A query
	// runs the server-side full-text search endpoint, scoped to the selected mailbox; with no query,
	// the same structured refinements apply to the current view via the ticket-list endpoint. Sort is
	// applied on the list endpoint — search results keep the server's recency ordering.
	const [searchInput, setSearchInput] = React.useState('');
	const [inboxFilters, setInboxFilters] = React.useState<InboxFilters>(DEFAULT_INBOX_FILTERS);
	const [searchPage, setSearchPage] = React.useState(1);
	const [settledMailboxId, setSettledMailboxId] = React.useState<string | null>(() => selectedMailboxId || null);
	const isMailboxTransition =
		!!selectedMailboxId && settledMailboxId !== null && settledMailboxId !== selectedMailboxId;
	const activeViewFilter =
		isMailboxTransition && requestedCategoryView ? (accountId ? 'all' : 'unassigned') : activeFilter;
	const isCategoryView = activeViewFilter.startsWith(CATEGORY_FILTER_PREFIX);
	const activeCategoryId = isCategoryView
		? activeViewFilter.slice(CATEGORY_FILTER_PREFIX.length) || undefined
		: undefined;
	const queryFilter = isCategoryView ? 'all' : activeViewFilter;
	const filters = isMailboxTransition ? clearMailboxScopedFilters(inboxFilters) : inboxFilters;
	const activeListPage = isMailboxTransition ? 1 : currentPage;
	const activeSearchPage = isMailboxTransition ? 1 : searchPage;
	const debouncedSearch = useDebounceValue(searchInput, 300);
	const trimmedSearch = debouncedSearch.trim();
	const isSearchDebouncing = searchInput.trim() !== trimmedSearch;
	const isTextSearch = trimmedSearch.length >= 2;
	const isSearchTransition = isSearchDebouncing && (searchInput.trim().length >= 2 || isTextSearch);
	const isResultTransition = isMailboxTransition || isSearchTransition;
	// Free text is the only thing that replaces the view: it has no preset to narrow. Every structured
	// refinement, CSM included, narrows the selected view on the list endpoint, so the views, the sort
	// and the column filters keep working while one is applied.
	const isSearching = isTextSearch;

	const teamQuery = useSupportTeamQuery(selectedMailboxId);
	const teamMembers = React.useMemo(() => sortSupportAgentsByName(teamQuery.data?.values ?? []), [teamQuery.data]);
	const agentTicketCountsQuery = useSupportAgentTicketCountsQuery(selectedMailboxId);
	const agentTicketCountsData = agentTicketCountsQuery.data;
	const agentTicketCounts = React.useMemo(() => toAgentTicketCountMap(agentTicketCountsData), [agentTicketCountsData]);

	const resetResultState = React.useCallback(
		(updates: Record<string, string | null> = {}) => {
			setSearchPage(1);
			setParams({ page: null, ticketId: null, threadId: null, ...updates });
		},
		[setParams]
	);
	const changeSearch = React.useCallback(
		(value: string) => {
			setSearchInput(value);
			resetResultState();
		},
		[resetResultState]
	);
	const changeFilters = React.useCallback(
		(next: InboxFilters) => {
			setInboxFilters(next);
			const assigneeView =
				next.assigneeId !== inboxFilters.assigneeId && ASSIGNEE_OWNING_VIEWS.has(activeViewFilter)
					? getViewForAssignee(next.assigneeId, currentUserId)
					: undefined;
			resetResultState(assigneeView ? { filter: assigneeView } : {});
		},
		[activeViewFilter, currentUserId, inboxFilters.assigneeId, resetResultState]
	);
	const clearFilters = React.useCallback(() => {
		setInboxFilters(DEFAULT_INBOX_FILTERS);
		resetResultState();
	}, [resetResultState]);

	React.useLayoutEffect(() => {
		if (!selectedMailboxId) return;
		if (settledMailboxId === null) {
			setSettledMailboxId(selectedMailboxId);
			return;
		}
		if (settledMailboxId === selectedMailboxId) return;

		setInboxFilters(current =>
			current.categoryId || current.assigneeId ? clearMailboxScopedFilters(current) : current
		);
		setSearchPage(1);

		if (mailboxUrlNeedsReset) {
			setParams({
				page: null,
				ticketId: null,
				threadId: null,
				...(requestedCategoryView ? { filter: null } : {}),
			});
			return;
		}

		setSettledMailboxId(selectedMailboxId);
	}, [mailboxUrlNeedsReset, requestedCategoryView, selectedMailboxId, setParams, settledMailboxId]);

	const listSort = sortToListParams(filters.sort);
	const selectedAccountId = accountId ?? filters.accountId;
	const ticketsQuery = useSupportTicketsQuery({
		filter: queryFilter,
		mailboxId: selectedMailboxId,
		accountId: selectedAccountId,
		categoryId: filters.categoryId ?? (isMailboxTransition ? undefined : activeCategoryId),
		status: filters.status,
		priority: filters.priority,
		assigneeId: filters.assigneeId,
		contact: filters.contact,
		csmId: filters.csmId,
		sort: listSort.sort,
		sortBy: listSort.sortBy,
		page: activeListPage,
		pageSize: PAGE_SIZE,
		enabled: !isSearching && !isResultTransition,
	});
	const tickets = ticketsQuery.data?.values ?? [];
	const totalCount = ticketsQuery.data?.totalCount ?? 0;
	const totalPages = Math.ceil(totalCount / PAGE_SIZE);

	// Search takes the same id-based refinements as the list endpoint and composes them with the
	// full-text query server-side.
	const searchQuery = useSupportTicketSearchQuery(
		isSearching && !isResultTransition
			? {
					query: trimmedSearch,
					mailboxId: selectedMailboxId,
					accountId: selectedAccountId,
					status: filters.status,
					priority: filters.priority,
					categoryId: filters.categoryId,
					assigneeId: filters.assigneeId,
					csmId: filters.csmId,
					page: activeSearchPage,
					pageSize: PAGE_SIZE,
				}
			: { query: '', page: 1, pageSize: PAGE_SIZE }
	);

	const myAgentConfigQuery = useMyAgentConfigQuery(selectedMailboxId);
	const canManageSettings = myAgentConfigQuery.data?.role === AgentRole.Admin;
	// Wait for membership to load; non-agent writes return 403.
	const isNotAnAgent = myAgentConfigQuery.data?.isAgent === false;
	// The scope param is inert outside Reports so returning to the tab restores the all-mailboxes view.
	const reportsScopeAll = screen === 'reports' && params.get('scope') === 'all';

	const selectFilter = (key: string) => {
		// Inactive views own the status dimension. A status refinement left over from another table view
		// would otherwise replace the preset and make the selected view silently show a different status.
		if (key === 'closed' || key === 'spam' || key === 'archive') {
			setInboxFilters(current => (current.status ? { ...current, status: undefined } : current));
		}
		resetResultState({ filter: key });
	};
	const selectAccount = (account: { id?: string; companyName?: string }) => {
		if (!account.id) return;
		resetResultState({
			accountId: account.id,
			accountName: account.companyName || null,
			filter: 'all',
		});
	};
	const clearAccount = () => {
		resetResultState({ accountId: null, accountName: null, filter: null });
	};
	const selectMailbox = (id: string) => {
		// The all-mailboxes choice only widens the reports scope; the persisted mailbox selection stands.
		if (id === ALL_MAILBOXES_VALUE) {
			setParams({ scope: 'all' });
			return;
		}
		setInboxFilters(current => clearMailboxScopedFilters(current));
		setSearchPage(1);
		setParams({
			scope: null,
			mailboxId: id,
			page: null,
			ticketId: null,
			threadId: null,
			...(requestedCategoryView ? { filter: null } : {}),
		});
	};

	// Notifications, GitHub issues and summary emails deep-link to a ticket, so opening one pushes on every
	// layout; otherwise Back from a linked ticket leaves the app instead of returning to the previous one.
	const selectTicket = (id: string) => setParams({ ticketId: id, threadId: null }, { push: true });
	// Only the phone masthead offers this, and there the ticket covers the list it returns to.
	const returnToInbox = () => setParams({ ticketId: null, threadId: null }, { push: true });
	const selectListPage = (page: number) => {
		setParams({ page: page === 1 ? null : String(page), ticketId: null, threadId: null });
	};
	const selectSearchPage = (page: number) => {
		setSearchPage(page);
		setParams({ page: null, ticketId: null, threadId: null });
	};

	// Both result sources arrive in server order; no per-page client sort may break cross-page ordering.
	const listTickets = isResultTransition ? [] : isSearching ? (searchQuery.data?.values ?? []) : tickets;

	// Search results carry their own pagination (searchPage); the filter view is paged via the URL.
	const list: TicketListSource = isSearching
		? {
				tickets: listTickets,
				isLoading: isResultTransition || searchQuery.isLoading,
				isError: !isResultTransition && searchQuery.isError,
				totalCount: isResultTransition ? 0 : (searchQuery.data?.totalCount ?? 0),
				page: activeSearchPage,
				totalPages: isResultTransition ? 0 : Math.ceil((searchQuery.data?.totalCount ?? 0) / PAGE_SIZE),
				onSelectPage: selectSearchPage,
			}
		: {
				tickets: listTickets,
				isLoading: isResultTransition || ticketsQuery.isLoading,
				isError: !isResultTransition && ticketsQuery.isError,
				totalCount: isResultTransition ? 0 : totalCount,
				page: activeListPage,
				totalPages: isResultTransition ? 0 : totalPages,
				onSelectPage: selectListPage,
			};

	const firstTicketId = listTickets[0]?.id;
	const hasSettledActiveResults =
		!isMailboxTransition &&
		!isSearchDebouncing &&
		!list.isLoading &&
		!list.isError &&
		(list.totalPages === 0 || list.page <= list.totalPages);
	const stableSelectedTicketId = isMailboxTransition ? undefined : selectedTicketId;
	// Render the first settled result immediately while the URL-sync effect catches up. An explicit
	// deep-link selection always wins, including while its surrounding result list is still loading.
	const activeTicketId =
		stableSelectedTicketId ??
		(!isPhoneLayout && screen === 'inbox' && inboxView === 'desk' && hasSettledActiveResults
			? firstTicketId
			: undefined);

	const { data: selectedTicket } = useSupportTicketQuery(activeTicketId || '');

	// A merged ticket keeps its id, so every link captured before the merge — GitHub issue bodies,
	// notifications, bookmarks — still addresses a conversation whose messages now live on the surviving
	// ticket. Follow the merge instead of rendering the empty shell it left behind. Reopening the shell
	// is the escape hatch for an agent who needs to look at it. A thread id rides along: a moved thread
	// keeps its id, so a deep link to one message still lands on that message.
	const [mergeRedirect, setMergeRedirect] = React.useState<{
		fromNumber: number;
		fromTicketId: string;
		toTicketId: string;
	} | null>(null);
	// Merges recorded before the pointer was stored are recovered from history, which nothing stopped from
	// pointing in a circle, so a walk follows each ticket at most once. The walk resets when the agent
	// lands somewhere this one did not send them, rather than leaving a ticket unfollowable for the session.
	const mergeWalk = React.useRef<{ visited: Set<string>; sentTo: string | null }>({ visited: new Set(), sentTo: null });
	React.useEffect(() => {
		// The reference is resolved server-side and can come back empty; the id alone is enough to navigate.
		const mergedIntoTicketId = selectedTicket?.mergedIntoTicket?.id || selectedTicket?.mergedIntoTicketId;
		if (!mergedIntoTicketId || selectedTicket.id !== selectedTicketId) return;
		// Reopening the shell — putting it back in a queue — is how an agent says they want it as it is.
		if (selectedTicket.status === TicketStatus.Active || selectedTicket.status === TicketStatus.Pending) return;
		if (selectedTicket.threadCount > 0) return;

		const walk = mergeWalk.current;
		if (walk.sentTo !== selectedTicket.id) walk.visited = new Set();
		if (walk.visited.has(selectedTicket.id)) {
			// The pointers loop. Stop here rather than claim this ticket's messages are somewhere else.
			setMergeRedirect(null);
			return;
		}

		walk.visited.add(selectedTicket.id);
		walk.sentTo = mergedIntoTicketId;
		setMergeRedirect({
			fromNumber: selectedTicket.number,
			fromTicketId: selectedTicket.id,
			toTicketId: mergedIntoTicketId,
		});
		setParams({ view: 'desk', ticketId: mergedIntoTicketId });
	}, [selectedTicket, selectedTicketId, setParams]);

	React.useEffect(() => {
		if (!mergeRedirect) return;
		if (activeTicketId === mergeRedirect.toTicketId || activeTicketId === mergeRedirect.fromTicketId) return;
		setMergeRedirect(null);
	}, [activeTicketId, mergeRedirect]);

	// Bumped when the user picks Forward from the ticket bar; ConversationPane watches it to open
	// the composer in forward mode (the composer state lives in that pane, not here).
	const [forwardSignal, setForwardSignal] = React.useState(0);
	const [isTicketListPinned, setTicketListPinned] = useLocalStorageState<boolean>(
		TICKET_LIST_PINNED_STORAGE_KEY,
		false
	);
	const [showChangeCustomerModal, setShowChangeCustomerModal] = React.useState(false);
	const [newTicketMode, setNewTicketMode] = React.useState<NewTicketMode | null>(null);
	const canCreateTicket = !isNotAnAgent;
	const openNewTicket = (mode: NewTicketMode) => setNewTicketMode(mode);
	const showStandaloneNewTicketDrawer =
		!isPhoneLayout && canCreateTicket && (screen !== 'inbox' || inboxView !== 'desk' || !activeTicketId);

	// After close/spam/archive/assign in the desk view, move to the neighboring ticket instead of staying on
	// one that just left the queue; the list refetch then drops the old row without emptying the pane.
	const advanceFromTicket = (fromTicketId: string) => {
		const index = listTickets.findIndex(ticket => ticket.id === fromTicketId);
		if (index < 0) return;
		const next = listTickets[index + 1] ?? listTickets[index - 1];
		setParams({ ticketId: next ? next.id : null, threadId: null });
	};

	const openTicketFromTable = (id: string) => setParams({ view: 'desk', ticketId: id, threadId: null }, { push: true });

	const showTableView = () => setParams({ view: null, ticketId: null, threadId: null }, { push: true });

	// Auto-select the first ticket so the conversation/details panes are populated on landing.
	// The table view has no selection to sync.
	React.useEffect(() => {
		if (screen !== 'inbox') return;
		if (inboxView !== 'desk') return;
		if (isPhoneLayout) return;
		if (selectedTicketId) return;
		if (!hasSettledActiveResults) return;
		if (!firstTicketId) return;
		setParams({ ticketId: firstTicketId, threadId: null });
	}, [screen, inboxView, isPhoneLayout, selectedTicketId, hasSettledActiveResults, firstTicketId, setParams]);

	React.useEffect(() => {
		if (screen !== 'inbox') return;
		if (isSearchDebouncing) return;
		if (list.totalPages <= 0) return;
		if (list.page <= list.totalPages) return;
		if (isSearching) {
			setSearchPage(list.totalPages);
			setParams({ page: null, ticketId: null, threadId: null });
			return;
		}
		setParams({ page: list.totalPages === 1 ? null : String(list.totalPages), ticketId: null, threadId: null });
	}, [screen, isSearchDebouncing, isSearching, list.page, list.totalPages, setParams]);

	React.useEffect(() => {
		setShowChangeCustomerModal(false);
	}, [activeTicketId]);

	// Keyed by ticket id rather than a bare flag, so a stale param left in the URL cannot pop the
	// composer open on whatever ticket is selected next.
	const autoOpenComposer = !!activeTicketId && params.get('compose') === activeTicketId;

	// Spend the signal as soon as it is read: left in the URL it would re-open the composer every time
	// the agent came back to this ticket, overriding whatever their saved draft says.
	React.useEffect(() => {
		if (!autoOpenComposer) return;
		setParams({ compose: null });
	}, [autoOpenComposer, setParams]);

	const activeNavItem = navItems.find(item => item.key === activeViewFilter);
	const accountFilterLabel = accountName ? `${accountName} tickets` : 'Account tickets';
	const filterLabel = isCategoryView
		? (categoryMap[activeViewFilter.slice(CATEGORY_FILTER_PREFIX.length)]?.name ?? 'Category')
		: accountId && activeViewFilter === 'all'
			? accountFilterLabel
			: (activeNavItem?.label ?? FILTER_LABELS[activeViewFilter] ?? 'Inbox');
	const viewLabel = isSearching ? 'Search results' : filterLabel;

	// Reading a thread, the list drops to its rail and peeks back on hover unless the agent pins it.
	const isTicketListCollapsed = !isPhoneLayout && !!activeTicketId && !isTicketListPinned;
	const listColumn = isTicketListCollapsed ? TICKET_LIST_RAIL_WIDTH_PX : TICKET_LIST_WIDTH_PX;
	const gridCols = isPhoneLayout ? 'minmax(0, 1fr)' : `${listColumn}px minmax(420px, 1fr) auto`;
	const settingsPath = selectedMailboxId
		? `/support/settings?mailboxId=${encodeURIComponent(selectedMailboxId)}`
		: '/support/settings';

	// The tab count follows the view on screen, so backgrounding the tab on Unassigned shows new
	// tickets arriving there. A transition's zero would read as "nothing left", so it is left off.
	const tabTicketCount =
		screen === 'inbox' && inboxView === 'table' && !list.isLoading && !list.isError ? list.totalCount : undefined;

	return (
		<AdminSupportPageLayout
			ticketCount={tabTicketCount}
			sidebarChildren={
				showStandaloneNewTicketDrawer ? (
					<SideDrawer>
						<AdminDrawerToolbar>
							<SupportNewTicketDrawerButton onSelect={openNewTicket} />
						</AdminDrawerToolbar>
					</SideDrawer>
				) : undefined
			}
		>
			<Portal destination={MastheadPortalId}>
				<SupportMastheadAppBar
					activeTab={screen}
					mailboxes={mailboxList}
					selectedMailboxId={reportsScopeAll ? ALL_MAILBOXES_VALUE : selectedMailboxId}
					onSelectInbox={() => setParams({ screen: null }, { push: true })}
					onSelectReports={() => setParams({ screen: 'reports' }, { push: true })}
					onSelectSettings={() => history.push(settingsPath)}
					onSelectMailbox={selectMailbox}
					showAllMailboxesOption={screen === 'reports'}
					showMailboxSwitcher={screen !== 'inbox' || inboxView === 'table' || isPhoneLayout}
					showSettings={canManageSettings}
					isTicketOpen={isPhoneLayout && !!activeTicketId}
					onBackToInbox={returnToInbox}
				>
					{!isPhoneLayout && screen === 'inbox' && inboxView === 'desk' && activeTicketId && selectedTicket && (
						<TicketTopBar
							ticket={selectedTicket}
							onForward={() => setForwardSignal(value => value + 1)}
							onChangeCustomer={() => setShowChangeCustomerModal(true)}
							onTicketLeftView={() => advanceFromTicket(selectedTicket.id)}
						/>
					)}
				</SupportMastheadAppBar>
			</Portal>

			{screen === 'reports' ? (
				<SupportReportsScreen
					mailboxId={reportsScopeAll ? null : selectedMailboxId}
					mailboxName={reportsScopeAll ? undefined : mailboxList.find(m => m.id === selectedMailboxId)?.name}
					allMailboxes={reportsScopeAll}
				/>
			) : (
				<div className={css(s.desk)}>
					<div className={css(s.deskColumn)}>
						{isNotAnAgent && (
							<div className={css(s.notAgentNotice)} role='status'>
								You're not an agent in this mailbox, so actions like replying, adding notes, and assigning tickets will
								be denied. Ask a mailbox admin to add you to the team.
							</div>
						)}
						{mergeRedirect && mergeRedirect.toTicketId === activeTicketId && (
							<div className={css(s.mergedTicketNotice)} role='status'>
								Ticket #{mergeRedirect.fromNumber} was merged into this one, so its messages are here.
							</div>
						)}
						<div className={css(s.deskBody)}>
							{isPhoneLayout && activeTicketId ? (
								<PhoneTicketStage
									ticketId={activeTicketId}
									autoOpenComposer={autoOpenComposer}
									targetThreadId={targetThreadId}
									onSelectTicket={selectTicket}
									onChangeCustomer={() => setShowChangeCustomerModal(true)}
									onTicketLeftView={() => advanceFromTicket(activeTicketId)}
								/>
							) : inboxView === 'table' ? (
								<TicketTableView
									list={list}
									navItems={navItems}
									activeFilter={activeViewFilter}
									onSelectFilter={selectFilter}
									isSearching={isSearching}
									searchValue={searchInput}
									onSearchChange={changeSearch}
									filters={filters}
									onFiltersChange={changeFilters}
									onClearFilters={clearFilters}
									categories={categories}
									teamMembers={teamMembers}
									agentTicketCounts={agentTicketCounts}
									categoryMap={categoryMap}
									showAccountColumn={!accountId}
									accountId={accountId}
									accountName={accountName}
									onSelectAccount={selectAccount}
									onClearAccount={clearAccount}
									onOpenTicket={openTicketFromTable}
								/>
							) : (
								<div className={css(s.paneGrid)} style={{ gridTemplateColumns: gridCols }}>
									<TicketListPane
										list={list}
										viewLabel={viewLabel}
										onBack={showTableView}
										onNewTicket={canCreateTicket ? openNewTicket : undefined}
										selectedTicketId={activeTicketId}
										onSelectTicket={selectTicket}
										navItems={navItems}
										categories={categories}
										activeFilter={activeViewFilter}
										onSelectFilter={selectFilter}
										isSearching={isSearching}
										searchValue={searchInput}
										onSearchChange={changeSearch}
										filters={filters}
										onFiltersChange={changeFilters}
										onClearFilters={clearFilters}
										teamMembers={teamMembers}
										agentTicketCounts={agentTicketCounts}
										categoryMap={categoryMap}
										collapse={
											!isPhoneLayout && activeTicketId
												? {
														isPinned: isTicketListPinned,
														onTogglePin: () => setTicketListPinned(pinned => !pinned),
													}
												: undefined
										}
									/>
									{/* A phone reaching this branch has no ticket open, so the list is the whole screen —
					    the conversation and details panes would only stack two dead placeholders under it. */}
									{!isPhoneLayout &&
										(activeTicketId ? (
											<ConversationPane
												ticketId={activeTicketId}
												forwardSignal={forwardSignal}
												autoOpenComposer={autoOpenComposer}
												targetThreadId={targetThreadId}
												onChangeCustomer={() => setShowChangeCustomerModal(true)}
												onTicketClosed={() => advanceFromTicket(activeTicketId)}
											/>
										) : (
											<div className={css(s.conversationPane)}>
												<div className={css(s.empty)}>Select a ticket to open the conversation.</div>
											</div>
										))}
									{!isPhoneLayout &&
										(activeTicketId ? (
											<RightPanel
												ticketId={activeTicketId}
												defaultTab='details'
												onSelectTicket={selectTicket}
												onChangeCustomer={() => setShowChangeCustomerModal(true)}
												onTicketLeftView={() => advanceFromTicket(activeTicketId)}
												onNewTicket={canCreateTicket ? openNewTicket : undefined}
											/>
										) : (
											<aside className={css(s.supportDrawerPlaceholder)}>
												<div className={css(s.empty)}>Select a ticket to see details.</div>
											</aside>
										))}
								</div>
							)}
						</div>
					</div>
				</div>
			)}
			{showChangeCustomerModal && activeTicketId && (
				<ChangeCustomerModal
					ticketId={activeTicketId}
					hasCustomer={!!selectedTicket?.contactEmail?.trim()}
					onClose={() => setShowChangeCustomerModal(false)}
				/>
			)}
			{newTicketMode && (
				<NewTicketModal
					mode={newTicketMode}
					defaultAccount={accountId ? { id: accountId, name: accountName } : undefined}
					defaultMailboxId={selectedMailboxId}
					onClose={() => setNewTicketMode(null)}
					onCreated={ticket =>
						setParams(
							{
								screen: null,
								view: 'desk',
								ticketId: ticket.id,
								threadId: null,
								// Outbound lands on an empty thread whose whole purpose is the email about to be written.
								compose: newTicketMode === 'outbound' ? ticket.id : null,
							},
							{ push: true }
						)
					}
				/>
			)}
		</AdminSupportPageLayout>
	);
};
